import React, { Component } from "react";
import { injectIntl, FormattedMessage } from "react-intl";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
  Chip, IconButton, CircularProgress, Box, Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import moment from "moment";

import Api from "../../services/api";
import { getActionsByBatch, normalizeAction } from "../../services/automation-execution.service";

// F3.2 — Drill-down do batch: lista todas as actions filhas de UMA execucao (batch_id).
// Consome GET /automation/executions/{batchId}/actions (F3.1).
//
// ROLAGEM (CLAUDE.md — erro recorrente):
//   NUNCA usar overflowY/maxHeight no sx do DialogContent — flex do MUI ignora.
//   Fix obrigatorio: DialogContent sx={{p:0}} envolvendo <div style={{maxHeight,overflowY:auto}}>.
class AutomationExecutionDetailDialog extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      actions: [],
      error: null,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && this.props.batchId && (this.props.batchId !== prevProps.batchId || !prevProps.open)) {
      this.load();
    }
  }

  async load() {
    this.setState({ loading: true, error: null, actions: [] });
    try {
      const result = await getActionsByBatch(this.props.batchId);
      if (result && result.data && result.data.success) {
        const raw = Array.isArray(result.data.data) ? result.data.data : (Array.isArray(result.data) ? result.data : []);
        this.setState({ actions: raw.map(normalizeAction), loading: false });
      } else {
        this.setState({ loading: false, error: this.props.intl.formatMessage({ id: "process.error" }) });
        Api.kickoff(result);
      }
    } catch (err) {
      const msg = (err && err.response && err.response.data && err.response.data.errors && err.response.data.errors[0])
        || this.props.intl.formatMessage({ id: "process.error" });
      this.setState({ loading: false, error: msg });
      Api.kickoff(err);
    }
  }

  statusIcon(status) {
    if (status === "SUCCESS") return <CheckCircleIcon sx={{ color: "#2e7d32" }} />;
    if (status === "FAILED") return <CancelIcon sx={{ color: "#c62828" }} />;
    return <RemoveCircleOutlineIcon sx={{ color: "#616161" }} />;
  }

  render() {
    const { open, onClose, batchId, intl } = this.props;
    const { loading, actions, error } = this.state;

    return (
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { maxHeight: "90vh", borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              <FormattedMessage id="automation.detail.title" />
            </Typography>
            <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#666" }}>
              batch_id: {batchId || "—"}
            </Typography>
          </div>
          <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>

        {/* DialogContent com sx={{p:0}} + div interno com overflow: garantia de rolagem correta (CLAUDE.md). */}
        <DialogContent dividers sx={{ p: 0 }}>
          <div style={{ maxHeight: "65vh", overflowY: "auto", padding: "16px 20px" }}>
            {loading && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            )}
            {!loading && error && (
              <Typography sx={{ color: "#c62828", textAlign: "center", py: 3 }}>{error}</Typography>
            )}
            {!loading && !error && actions.length === 0 && (
              <Typography sx={{ textAlign: "center", py: 4, color: "#888" }}>
                <FormattedMessage id="automation.detail.empty" />
              </Typography>
            )}
            {!loading && !error && actions.length > 0 && actions.map((a, idx) => (
              <Box key={a.id || idx} sx={{ mb: 2, border: "1px solid #e0e0e0", borderRadius: 2, p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                  {this.statusIcon(a.status)}
                  <Chip label={`#${a.executionOrder}`} size="small" sx={{ backgroundColor: "#f5f5f5", fontFamily: "monospace" }} />
                  <Typography sx={{ fontWeight: 600 }}>{a.actionTypeCode}</Typography>
                  <Typography sx={{ color: "#888", fontSize: 12, fontFamily: "monospace" }}>
                    {a.handlerName}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  <Typography sx={{ color: "#888", fontSize: 12 }}>
                    {typeof a.durationMs === "number" ? `${a.durationMs}ms` : ""}
                  </Typography>
                </Box>
                <Divider sx={{ mb: 1 }} />
                <Box sx={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 0.5, fontSize: 13 }}>
                  <div style={{ color: "#666" }}>Chamado:</div>
                  <div>{a.calledId || "—"}</div>
                  <div style={{ color: "#666" }}>Started:</div>
                  <div>{a.startedAt ? moment(a.startedAt).format("DD/MM/YYYY HH:mm:ss") : "—"}</div>
                  <div style={{ color: "#666" }}>Status:</div>
                  <div>{a.status}</div>
                  {a.errorCode && (
                    <>
                      <div style={{ color: "#666" }}>Erro:</div>
                      <div style={{ color: "#c62828", fontFamily: "monospace", fontSize: 12 }}>
                        {a.errorCode}{a.errorMessage ? ` — ${a.errorMessage}` : ""}
                      </div>
                    </>
                  )}
                  {a.outputMetadata && (
                    <>
                      <div style={{ color: "#666" }}>Output:</div>
                      <div style={{
                        backgroundColor: "#1a1a2e", color: "#e0e0e0", padding: 12, borderRadius: 6,
                        fontFamily: "monospace", fontSize: 12, overflowX: "auto", whiteSpace: "pre-wrap",
                      }}>
                        {typeof a.outputMetadata === "string" ? a.outputMetadata : JSON.stringify(a.outputMetadata, null, 2)}
                      </div>
                    </>
                  )}
                </Box>
              </Box>
            ))}
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>{intl.formatMessage({ id: "close" })}</Button>
        </DialogActions>
      </Dialog>
    );
  }
}

export default injectIntl(AutomationExecutionDetailDialog);
